import React, { FC, useEffect } from 'react';

import { AggregatedAlertDetails, LogActions } from '@ariksa/notification/api';
import {
  Divider,
  HStack,
  Stack,
  Text,
  GridItem,
  Grid,
  Box,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { each, entries, isEmpty, slice, values } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';
import { Dict } from 'types/utils';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import {
  DetailsIdentifier,
  ExpandableText,
  formatTooltip,
  OrderedList,
  PlusCountTag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { LinkText } from 'components/DataDisplay/Text/LinkText';
import { renderCompliance } from 'components/DataDisplay/Utils/renderCompliance';
import { IconTypes } from 'components/Icons';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { EnvironmentName } from 'containers/App/utils';
import {
  getAlertStatusMapping,
  renderAlertDrawerIntegrations,
} from 'containers/Findings/AlertDrawer/Components/Aggregated/utils';
import { selectAlertDrawer } from 'containers/Findings/AlertDrawer/selectors';
import { AlertStatus } from 'containers/Findings/FindingsOverview/Components/AlertTables/Components/AlertStatus';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';

import { findingTicketStatusCounts } from './findingTicketStatus';
import { useFindingTickets } from './hooks/useFindingTickets';
import { IndividualAlertTable } from './IndividualAlertTable';

interface IAggregatedAlertDrawerContent {
  alert: AggregatedAlertDetails;
  tabIndex: number;
  setTabIndex: (value: number) => void;
}

export const FindingDrawerContent: FC<IAggregatedAlertDrawerContent> = props => {
  const { alert } = props;
  const navigate = useNavigate();

  const { compliance, alertsByRuleId } = useSelector(selectFindingsOverview);
  const { alertListStatus } = useSelector(selectAlertDrawer);
  const [integrations, setIntegrations] = React.useState<LogActions[]>([]);
  // Alert Vault keeps finding tickets itself; legacy rows use ticket_details.
  const alertVault = isAlertVaultEnabled();
  const findingTickets = useFindingTickets(alertVault, alert?.group_id);

  useEffect(() => {
    let integrationsSet = new Set<LogActions>();
    each(alertListStatus.data, o => {
      each(o, t => integrationsSet.add(t.action));
    });
    setIntegrations(Array.from(integrationsSet));
  }, [alertListStatus.data]);

  const renderAlertStatus = () => {
    if (alertVault) {
      // Counts come from the finding's total open alerts and its sub-tasks,
      // not from the alerts on the drawer's current page.
      const counts = findingTicketStatusCounts(
        Number(alert.total_results ?? 0),
        findingTickets.subtasks,
      );
      if (isEmpty(counts)) return <Text>-</Text>;
      return (
        <Wrap spacing={3}>
          {entries(counts).map(([k, v]) => (
            <WrapItem key={k}>
              <AlertStatus status={getAlertStatusMapping(k)} count={v} />
            </WrapItem>
          ))}
        </Wrap>
      );
    }
    const tickets = values(alertListStatus.data);
    const status: Dict<number> = {
      'To Do': alertsByRuleId.data?.length - tickets?.length ?? 0,
    };
    if (!tickets.length && !alertsByRuleId.data?.length) {
      return <Text>-</Text>;
    }
    tickets.forEach(ts => {
      ts.forEach(t => {
        if (
          t.action === LogActions.Jira ||
          t.action === LogActions.Servicenow
        ) {
          const ticketStatus = t.ticket_status;
          status[ticketStatus!] = status[ticketStatus!] ?? 0;
          status[ticketStatus!]++;
        }
      });
    });

    return (
      <Wrap spacing={3}>
        {entries(status).map(([k, v]) => {
          return (
            <WrapItem>
              <AlertStatus status={getAlertStatusMapping(k)} count={v} />
            </WrapItem>
          );
        })}
      </Wrap>
    );
  };

  return (
    <Stack flex={1} w="full" h="full" spacing={5}>
      <HStack>
        <Grid templateColumns="repeat(5, 1fr)" gap={4} w="full">
          <GridItem colSpan={2}>
            <DetailsIdentifier
              label="Description"
              value={
                <ExpandableText noOfLines={6}>
                  {alert.description}
                </ExpandableText>
              }
              isInline={false}
            />
          </GridItem>
          <GridItem colSpan={1}>
            <Stack spacing={4}>
              <DetailsIdentifier
                isInline={false}
                label="Severities"
                value={renderVulnerabilities({
                  value: alert.severity,
                  spacing: 4,
                })}
              />
              <DetailsIdentifier
                isInline={false}
                label="Generated By"
                value={
                  isEmpty(alert?.blueprints) ? (
                    'None'
                  ) : (
                    <HStack>
                      {alert?.blueprints?.[0] && (
                        <LinkText
                          onClick={() =>
                            navigate(
                              `/policy-hub/blueprint/${alert?.blueprints?.[0]?.blueprint_id}`,
                            )
                          }
                          fontSize="md"
                        >
                          {alert?.blueprints?.[0]?.blueprint_name}
                        </LinkText>
                      )}
                      {(alert?.blueprints?.length ?? 0) > 1 && (
                        <PlusCountTag
                          count={(alert?.blueprints?.length ?? 0) - 1}
                          tooltip={formatTooltip({
                            header: 'Blueprints',
                            content: (
                              <OrderedList
                                list={slice(
                                  alert?.blueprints,
                                  1,
                                  alert?.blueprints?.length,
                                ).map(b => b.blueprint_name)}
                              ></OrderedList>
                            ),
                          })}
                        />
                      )}
                    </HStack>
                  )
                }
              />
              <DetailsIdentifier
                isInline={false}
                label="Compliance Frameworks"
                value={renderCompliance(
                  compliance.data?.[alert.alert_rule_ids?.[0]!] ?? [],
                )}
                isLoading={compliance.isLoading}
              />
            </Stack>
          </GridItem>
          <GridItem colSpan={1}>
            <Stack spacing={4}>
              <DetailsIdentifier
                isInline={false}
                label="Environment"
                value={
                  <WithResourceIcon
                    resourceType={IconTypes.Environment}
                    iconFilled={false}
                    iconSize="xs"
                    color={customTheme.colors.gray[250]}
                    spacing={2}
                  >
                    <EnvironmentName
                      environmentId={alert.environment_id ?? ''}
                    />
                  </WithResourceIcon>
                }
              />
              <DetailsIdentifier
                isInline={false}
                label="Accounts"
                value={<Text>{alert.account_id_list?.length ?? 0}</Text>}
              />
              <DetailsIdentifier
                isInline={false}
                label="Integrations"
                value={
                  isEmpty(integrations)
                    ? 'None'
                    : renderAlertDrawerIntegrations(integrations)
                }
                isLoading={
                  alertListStatus.isLoading || alertsByRuleId.isLoading
                }
              />
            </Stack>
          </GridItem>
          <GridItem colSpan={1}>
            <Stack spacing={4}>
              <DetailsIdentifier
                isInline={false}
                label="Parent Ticket"
                value={
                  alertVault && findingTickets.parent ? (
                    <LinkText
                      onClick={() =>
                        window.open(
                          findingTickets.parent?.browse_link,
                          '_blank',
                        )
                      }
                    >
                      {findingTickets.parent.external_id}
                    </LinkText>
                  ) : (
                    alert?.ticket_details?.ticket_id ?? 'None'
                  )
                }
                isLoading={
                  alertListStatus.isLoading || alertsByRuleId.isLoading
                }
              />
              <DetailsIdentifier
                isInline={false}
                label="Status of Issues"
                value={renderAlertStatus()}
                isLoading={
                  alertListStatus.isLoading || alertsByRuleId.isLoading
                }
              />
            </Stack>
          </GridItem>
        </Grid>
      </HStack>
      <Divider color="gray.200" />

      <Box flex={1} w="full" h="full">
        <IndividualAlertTable aggregatedAlert={alert} />
      </Box>
    </Stack>
  );
};
